import React from 'react'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {toast} from 'react-toastify'
import {fetchConnectionTestHistory, requestConnectionTest} from './api'
import {ConnectionTestResult} from './types'

type Props = {
  userId: string
}

const BRAND = 'rgb(163, 162, 66)'

const parseServerUtc = (iso: string): Date => {
  if (!iso) return new Date(NaN)
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso)
  return new Date(hasTz ? iso : `${iso}Z`)
}

const formatDate = (iso: string) => {
  try {
    const d = parseServerUtc(iso)
    if (isNaN(d.getTime())) return {date: iso, time: ''}
    const date = d.toLocaleDateString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    })
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    })
    return {date, time}
  } catch {
    return {date: iso, time: ''}
  }
}

const StatusBadge: React.FC<{isStable: boolean}> = ({isStable}) =>
  isStable ? (
    <span
      className='badge fw-semibold'
      style={{backgroundColor: '#E7F4D9', color: '#6B8E23', padding: '6px 10px', borderRadius: 6}}
    >
      <i className='bi bi-check-circle me-1'></i>
      Good
    </span>
  ) : (
    <span
      className='badge fw-semibold'
      style={{backgroundColor: '#FDE2E4', color: '#C0392B', padding: '6px 10px', borderRadius: 6}}
    >
      <i className='bi bi-exclamation-circle me-1'></i>
      Please Fix Internet
    </span>
  )

const AdminConnectionTestTab: React.FC<Props> = ({userId}) => {
  const queryClient = useQueryClient()
  const queryKey = ['connection-test-history', userId]

  const {data, isLoading, isError} = useQuery<ConnectionTestResult[]>(
    queryKey,
    async () => {
      const res = await fetchConnectionTestHistory(userId)
      return res.data || []
    },
    {cacheTime: 0, refetchOnWindowFocus: false, enabled: !!userId}
  )

  const requestMutation = useMutation(() => requestConnectionTest(userId), {
    onSuccess: () => {
      toast.success(
        'Connection test requested. The interpreter will be prompted on their next login.'
      )
      queryClient.invalidateQueries(queryKey)
    },
    onError: (err: any) => {
      const message = err?.response?.data?.text || 'Failed to request connection test'
      toast.error(message)
    },
  })

  const history = data || []

  return (
    <div className='pt-5'>
      <div
        className='card shadow-sm mb-5'
        style={{borderRadius: 10, border: '1px solid #EDEEF1'}}
      >
        <div className='card-body p-6'>
          <p className='text-gray-700 fs-6 mb-4'>
            Interpreter connection stability to ensure high-quality interpretation.
          </p>
          <button
            type='button'
            className='btn fw-semibold d-inline-flex align-items-center'
            style={{backgroundColor: BRAND, color: '#fff'}}
            onClick={() => requestMutation.mutate()}
            disabled={requestMutation.isLoading}
          >
            <i className='bi bi-activity me-2'></i>
            {requestMutation.isLoading ? 'Requesting...' : 'Request Connection Test'}
          </button>
        </div>
      </div>

      <div className='card shadow-sm' style={{borderRadius: 10, border: '1px solid #EDEEF1'}}>
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold d-flex align-items-center' style={{fontSize: 16}}>
              <i className='bi bi-activity me-2' style={{color: BRAND}}></i>
              Connection Test History
            </span>
          </h3>
        </div>
        <div className='card-body pt-3'>
          {isLoading && (
            <div className='text-center py-10 text-gray-600'>Loading history...</div>
          )}
          {isError && (
            <div className='text-center py-10 text-danger'>
              Unable to load connection test history.
            </div>
          )}
          {!isLoading && !isError && history.length === 0 && (
            <div className='text-center py-10 text-gray-600'>
              No connection tests recorded for this interpreter yet.
            </div>
          )}
          {!isLoading && !isError && history.length > 0 && (
            <div className='table-responsive'>
              <table className='table align-middle gs-0 gy-3'>
                <thead>
                  <tr className='text-gray-500 text-uppercase fs-8 fw-semibold'>
                    <th style={{minWidth: 180}}>Timestamp</th>
                    <th style={{minWidth: 120}}>Latency</th>
                    <th style={{minWidth: 120}}>Jitter</th>
                    <th style={{minWidth: 160}}>Result Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row, idx) => {
                    const {date, time} = formatDate(row.createdDateTime)
                    const isNewest = idx === 0
                    return (
                      <tr
                        key={row.code}
                        style={
                          isNewest
                            ? {
                                backgroundColor: '#FCFBEF',
                                borderLeft: `3px solid ${BRAND}`,
                              }
                            : undefined
                        }
                      >
                        <td className='ps-4'>
                          <div className='d-flex flex-column'>
                            <span className='fw-bold text-dark'>
                              {date}
                              {isNewest && (
                                <span
                                  className='badge ms-2 fw-semibold'
                                  style={{
                                    backgroundColor: '#F4F4E4',
                                    color: BRAND,
                                    fontSize: 10,
                                  }}
                                >
                                  NEWEST
                                </span>
                              )}
                            </span>
                            {time && (
                              <span className='text-gray-500 fs-8'>
                                <i className='bi bi-clock me-1'></i>
                                {time}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className='fw-bold text-dark'>{row.latencyMs}</span>
                          <span className='text-gray-500 ms-1 fs-8'>ms</span>
                        </td>
                        <td>
                          <span className='fw-bold text-dark'>{row.jitterMs}</span>
                          <span className='text-gray-500 ms-1 fs-8'>ms</span>
                        </td>
                        <td>
                          <StatusBadge isStable={row.isStable} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminConnectionTestTab
